import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { formatDate } from 'react-day-picker/moment';
import { DateTime } from 'luxon';
import { Modal } from 'react-responsive-modal';
import { useGlobalState } from 'state-pool';
import _ from 'lodash';

import Label from '../../../components/label';
import Select from '../../../components/select';
import SelectCreate from '../../../components/select-create';
import Note from '../../../components/note';
import DayPicker from '../../../components/day-picker';
import ActionButtonNoPrint from '../../../components/action-button-no-print';
import ClickButton from '../../../components/click-button';
import InputNumeral from '../../../components/input-numeral';
import TextNumber from '../../../components/text-number';
import LinkButton from '../../../components/link-button';
import { getData, postData } from '../../../helper/request-response';
import { onSuccess } from '../../../helper/toaster';
import { getSellingPrice } from '../../../helper/util';
import { getRetailBalance } from '../../../helper/cash-balance';
import constant from '../../../config/constant';
import purchasePath from './config';

const Action = () => {

    const data = useLocation();
    const refData = useRef(data);
    const history = useHistory();

    const [, , setRetailBalance] = useGlobalState('retailBalance');

    /**
     * [titleInfo] state
     */
    const [titleInfo, setTitleInfo] = useState("");

    /**
     * [modalIsOpen] state
     */
    const [modalIsOpen, setIsOpen] = useState(false);

    /**
     * [isDisabledSave] state
     */
    const [isDisabledSave, setIsDisabledSave] = useState(true);

    /**
     * [isDisabledAdd]
     */
    const [isDisabledAdd, setIsDisabledAdd] = useState(true);

    /**
     * [isView] state
     */
    const [isNew, setIsNew] = useState(true);

    /**
     * [idxItem] state
     */
    const [idxItem, setIdxItem] = useState(-1);

    const [selectedGnsCode, setSelectedGnsCode] = useState({ value:'', label:'' });

    const [selectedGnsName, setSelectedGnsName] = useState({ value:'', label:'' });

    const [selectedUnit, setSelectedUnit] = useState({ value:'', label:''});

    const [selectedSupplier, setSelectedSupplier] = useState({ value:'', label:'' });

    const [coa, setCoa] = useState([]);

    /**
     * [purchase] state
     */
    const [purchase, setPurchase] = useState({
        id: 0,
        invoiceNo: `RP${DateTime.local().toFormat(constant.INVOICE_FORMAT)}`,
        trxDate: DateTime.local(),
        lineNo: 0,
        isCommit: 1,
        supplierId: 0,
        supplierName: "",
        totalPurchase: 0,
        payment: 0,
        outstanding: 0,
        apArId: 0,
        items:[]
    });

    const gnsEntity = {
        id: 0,
        code: '',
        name: '',
        purchasePrice: 0,
        sellingPrice: 0,
        qty: 0,
        isSubtrahend: false,
        unitId: 0,
        businessUnitId: 2,
        coaId: 0,
        unit: ''
    }

    /**
     * [goodsAndService] state
     */
    const [gns, setGns] = useState(gnsEntity);

    /**
     * Goods and services coa changed
     * @param {Object} e - Input textfield.
     */
    const onCoaChange = e => {

        const { name, value } = e.target;

        setGns(data => ({
            ...data,
            [name]: value
        }));
    }

    /**
     * Update goodsAndService object
     * @param {Object} opt 
     */
    const setGnsState = opt => {

        setGns(gns => ({
            ...gns,
            id: opt.id,
            code: opt.code,
            name: opt.name,
            purchasePrice: opt.purchasePrice,
            sellingPrice: opt.sellingPrice,
            isSubtrahend: opt.isSubtrahend,
            unitId: opt.unitId,
            businessUnitId: opt.buId,
            coaId: opt.coaId,
            unit: opt.unit
        }));
    }

    const setSelectedState = opt => {

        setSelectedGnsCode(code => ({
            ...code,
            value: opt.id,
            label: opt.code
        }));

        setSelectedGnsName(name => ({
            ...name,
            value: opt.id,
            label: opt.name
        }));

        setSelectedUnit(unit => ({
            ...unit,
            value: opt.unitId,
            label: opt.unit
        }));

    }

    /**
     * Reset state of [goodsAndServices] and [updateItem]
     */
    const resetGns = () => {

        setGns(gnsEntity);

        setSelectedGnsCode(code => ({
            ...code,
            value: '',
            label: ''
        }));

        setSelectedGnsName(name => ({
            ...name,
            value: '',
            label: ''
        }));

        setSelectedUnit(unit => ({
            ...unit,
            value: '',
            label: ''
        }));

    }

    /**
     * Autocomplete when code of [GoodsAndService] field are changed
     * @param {Object} opt - { id: string, label: string}
     */
    const onGnsCodeChange = opt => {

        if (opt.id) {

            setGnsState(opt);
            
            setSelectedState(opt);

        } else {

            setGns(gns => ({
                ...gns,
                code: opt.label
            }));

            setSelectedGnsCode(code => ({
                ...code,
                value: 0,
                label: opt.label
            }));
        }
    }

    /**
     * Autocomplete when name of [GoodsAndService] field are changed
     * @param {Object} opt - { id: string, label: string}
     */
    const onGnsNameChange = opt => {

        if (opt.id) {

            setGnsState(opt);

            setSelectedState(opt);

        } else {

            setGns(gns => ({
                ...gns,
                name: opt.label
            }));

            setSelectedGnsName(code => ({
                ...code,
                value: 0,
                label: opt.label
            }));
        }
    }

    /**
     * Autocomplete when code of [Unit] field are changed.
     * @param {Object} opt - { id: string, label: string}
     */
    const onUnitChange = opt => {

        if (opt.id) {

            setGns(data => ({
                ...data,
                unitId: opt.id,
                unit: opt.code
            }));

            setSelectedUnit(unit => ({
                ...unit,
                value: opt.id,
                label: opt.code
            }));
        }
    }

    const onQtyChange = values => {

        const { value } = values;

        setGns(gns => ({
            ...gns,
            qty: parseFloat(value)
        }));

    }

    const onPurchasePriceChange = values => {

        const { value } = values;

        setGns(gns => ({
            ...gns,
            purchasePrice: parseFloat(value),
            sellingPrice: getSellingPrice(parseFloat(value))
        }));

    }

    const onSellingPriceChange = values => {

        const { value } = values;

        setGns(gns => ({
            ...gns,
            sellingPrice: parseFloat(value)
        }));

    }

    /**
     * Calculate total of purchased items from supplier.
     * @param {Array} items 
     */
    const calculateTotal = items => {

        let total = 0;

        for(let i = 0; i < items.length; i++) {

            const qty = parseFloat(items[i].qty);
            const purchasePrice = parseFloat(items[i].purchasePrice);
            total += qty * purchasePrice;

        }

        return total;

    };

    /**
     * Event triggered when button add new item clicked.
     * @param {Object} e 
     */
    const onNewItem = e => {

        e.preventDefault();

        resetGns();

        setIdxItem(-1);

    }

    /**
     * Event triggered when [GoodsAndServices] added to purchased item.
     * @param {Object} e 
     */
    const onAddItem = e => {

        e.preventDefault();

        /**
         * Add [GoodsAndServices] to purchased items
         */
        const addPurchasedItem = () => {

            let gsItems = purchase.items;
            gsItems.push(gns);

            return gsItems;

        }

        /**
         * Update [GoodsAndServices] in purchased items based on [idxItem] state.
         */
        const updatePurchasedItem = () => {

            let gsItems = purchase.items;

            for (let i = 0; i < gsItems.length; i++) {

                if (i === idxItem) {

                    gsItems[i].id = gns.id;
                    gsItems[i].code = gns.code;
                    gsItems[i].name = gns.name;
                    gsItems[i].purchasePrice = gns.purchasePrice;
                    gsItems[i].sellingPrice = gns.sellingPrice;
                    gsItems[i].qty = gns.qty;
                    gsItems[i].unitId = gns.unitId;
                    gsItems[i].unit = gns.unit;
                    gsItems[i].businessUnitId = gns.businessUnitId;

                    break;
                } 
            }

            return gsItems;

        }

        let gsItems = [];

        if (idxItem < 0) gsItems = addPurchasedItem();
        else gsItems = updatePurchasedItem();

        const total = calculateTotal(gsItems);

        setPurchase(data => ({
            ...data,
            totalPurchase: total,
            outstanding: total,
            items: gsItems
        }));

        resetGns();

        setIdxItem(-1);

        closeModal(e);
    }

    /**
     * Autocomplete when [Supplier] field is changed.
     * @param {Object} opt - { id: string, label: string}
     */
    const onSupplierChange = opt => {

        if (opt.id) {

            setPurchase(data => ({ 
                ...data,
                apArId: opt.apArId,
                supplierId: opt.id,
                supplierName: opt.name
            }));

            setSelectedSupplier(supplier => ({
                ...supplier,
                value: opt.id,
                label: opt.name
            }));

        } 

    }

    /**
     * Event triggerd when transaction date is selected.
     * @param {string} day 
     */
    const onDayChange = day => {

        setPurchase(data => ({ 
            ...data,
            trxDate: formatDate(day, constant.DEFAULT_DATE)
        }));

    }

    /**
     * Open modal
     * @param {Object} e 
     */
    const openModal = (e) => {

        e.preventDefault();

        setIsOpen(true);

    };

    /**
     * Close modal
     * @param {Object} e 
     */
    const closeModal = (e) => {
        
        e.preventDefault();

        setIsOpen(false);

    }

    /**
     * Event trigered when item in purchased items is clicked.
     * @param {Object} e 
     * @param {Number} index 
     */
    const onViewDetail = (e, index) => {

        const item = purchase.items[index];

        setGnsState(item);

        setGns(data => ({
            ...data,
            qty: item.qty
        }));

        setIdxItem(index);

        openModal(e);
    }

    /**
     * Event triggered when amount payment changed.
     * @param {Object} values - Object from InputNumeral. 
     */
    const onPaymentChange = values => {

        const { value } = values;

        const gsItems = purchase.items;
        
        const total = calculateTotal(gsItems);

        const outstanding = parseFloat(total) - value;

        setPurchase(purchase => ({
            ...purchase,
            outstanding: outstanding,
            payment: value
        }));

    }

    /**
     * Event trigered when main form of purchase is submited
     * @param {object} e 
     */
    const onSubmit = (e) => {

        e.preventDefault();

        setRetailBalance(0);

        /**
         * Create new purchase data
         */
        const post = async () => {

            const result = await postData(purchasePath.base, purchase);

            if (!_.isEmpty(result)) {

                onSuccess(`Pembelian ${purchase.invoiceNo} berhasil di tambahkan`);

                history.goBack();

                getRetailBalance();
            }
        };

        if (purchase.totalPurchase) post();
        
    }

    const getPurchaseDetail = async (id) => {

        const result = await getData(`${purchasePath.base}/${id}`);

        return result;
    }


    useEffect(() => {

        const getCoa = async () => {

            const result = await getData(purchasePath.coaGns);

            setCoa(result);
            
        }

        const init = () => {

            const state = refData.current.state;

            if (state) {
    
                setPurchase(state);

                setIsNew(false);
                
                setTitleInfo(`Detail: ${state.invoiceNo}`);

                setSelectedSupplier(supplier => ({
                    ...supplier,
                    value: state.supplierId,
                    label: state.supplierName
                }));

                getPurchaseDetail(state.id)
                    .then(result => {

                        setPurchase(data => ({
                            ...data,
                            items: result
                        }));

                    });
    
            } else {
    
                setTitleInfo("Tambah Pembelian Baru");
                
            }
        }

        getCoa();

        init();

    }, []);

    useEffect(() => {

        if (purchase.invoiceNo && purchase.supplierId && purchase.trxDate) setIsDisabledAdd(false);

        if (purchase.totalPurchase) setIsDisabledSave(false);

    }, [purchase.invoiceNo, purchase.supplierId, purchase.trxDate, purchase.totalPurchase]);

    return(
        <React.Fragment>
            <div className="card-header">
                <h3 className="h6 text-uppercase mb-0">{ purchasePath.title } | { titleInfo }</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <Modal
                        open={ modalIsOpen }
                        onClose={ closeModal }>
                            <div className="modal-header">
                                <h4 id="purchase-title" className="modal-title">Tambah Detail Pembelian</h4>
                            </div>
                            <div className="modal-body">
                                <form>

                                    <div className="form-group">
                                        <Label htmlFor="gns-name" text="Nama Barang" isMandatory={ true } value={ gns.name } />
                                        <SelectCreate
                                            id="gns-name"
                                            api={ purchasePath.gnsFind }
                                            entityId="2"
                                            value={ selectedGnsName }
                                            onChange={ onGnsNameChange }
                                        />
                                    </div>

                                    <div className="form-group">
                                        <Label htmlFor="gns-code" text="Kode Barang" isMandatory={ true } value={ gns.code } />
                                        <SelectCreate 
                                            id="gns-code"
                                            api={ purchasePath.gnsFind }
                                            value={ selectedGnsCode }
                                            onChange={ onGnsCodeChange }
                                        />
                                    </div>

                                    <div className="form-group">
                                        <Label htmlFor="gns-type" text="Kategori" isMandatory={ true } value={ gns.coaId } />
                                        <select id="gns-type" className="form-control" name="coaId" onChange={ e => onCoaChange(e) } value={ gns.coaId }>
                                            <option value="">Pilih</option>
                                            { coa.map(({id, description}) => {return(<option key={id} value={id}>{description}</option>)}) }
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <Label htmlFor="unit" text="Satuan" isMandatory={ true } value={ gns.unit } />
                                        <Select 
                                            id="unit"
                                            api={ purchasePath.unitFilter }
                                            value={ selectedUnit }
                                            onChange={ onUnitChange }
                                        />
                                        <span><LinkButton to={ purchasePath.newUnit } className="stretched-link" label="Tambah Satuan Baru" /></span>
                                    </div>

                                    <div className="form-group">
                                        <Label htmlFor="qty" text="Quantity" isMandatory={ true } value={ gns.qty } />
                                        <InputNumeral
                                            id="qty"
                                            value={ gns.qty }
                                            onValueChange={ onQtyChange }
                                        />
                                    </div>

                                    <div className="form-group">
                                        <Label htmlFor="purchase-price" text="Harga Beli" isMandatory={ true } value={ gns.purchasePrice } />
                                        <InputNumeral
                                            id="purchase-price"
                                            value={ gns.purchasePrice }
                                            onValueChange={ onPurchasePriceChange }
                                        />
                                    </div>

                                    <div className="form-group">
                                        <Label htmlFor="selling-price" text="Harga Jual" isMandatory={ true } value={ gns.sellingPrice } />
                                        <InputNumeral
                                            id="selling-price"
                                            value={ gns.sellingPrice }
                                            onValueChange={ onSellingPriceChange }
                                        />
                                    </div>

                                    <div className="form-group">
                                        <Note isAction={ true } hideHeader={ true } />
                                    </div>
                                </form>
                            </div>

                            <div className="modal-footer">
                                {
                                    gns.code &&
                                    <>
                                        <ClickButton onClick={ onNewItem } className="btn btn-info" />&nbsp;
                                    </>
                                    
                                }
                                <ClickButton onClick={ onAddItem } className="btn btn-success" label={ constant.SAVE } />
                            </div>
                    </Modal>
                </div>
                
                <div className="row">
                    <div className="col-md-6 border-right">
                        <form>
                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="inv-no" text="No Faktur" isMandatory={ true } value={ purchase.invoiceNo } />
                                    <input 
                                        id="inv-no" 
                                        type="text" 
                                        className="form-control"
                                        value={ purchase.invoiceNo }
                                        disabled />
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="supplier" text="Supplier" isMandatory={ true } value={ purchase.supplierId } />
                                    <Select 
                                        id="supplier"
                                        api={ purchasePath.custFind }
                                        entityId="6"
                                        value={ selectedSupplier }
                                        onChange={ onSupplierChange }
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="trx-date" text="Tanggal" isMandatory={ true } value={ purchase.trxDate } />
                                    <DayPicker id="trx-date" name="trxDate" valueDate={ purchase.trxDate } onDayChange={ onDayChange } />
                                </div>
                            </div>

                        </form>
                    </div>

                    <div className="col-md-6">
                        <form onSubmit={ e => onSubmit(e) }>

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label htmlFor="total" text="Total" />
                                    <InputNumeral 
                                        id="total"
                                        value={ purchase.totalPurchase }
                                        display={ true }
                                    />
                                </div>
                            </div>

                            {
                                isNew &&
                                <>
                                    <div className="form-group row">
                                        <div className="col-md-12">
                                            <Label htmlFor="payment" text="Jumlah Bayar" />
                                            <InputNumeral 
                                                id="payment"
                                                value={ purchase.payment }
                                                onValueChange={ onPaymentChange }
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group row">
                                        <div className="col-md-12">
                                            <Label htmlFor="outstanding" text="Sisa Pembayaran" />
                                            <InputNumeral 
                                                id="outstanding"
                                                value={ purchase.outstanding }
                                                display={ true }
                                            />
                                        </div>
                                    </div>
                                </>  
                            }

                            <div className="form-group row">
                                <div className="col-md-12">
                                    <Label />
                                    <ActionButtonNoPrint 
                                        isSave={ isNew }
                                        isCancel={ false }
                                        isDisabledSave={ isDisabledSave }
                                        isDisabledAdd={ isDisabledAdd }
                                        isModal={ true }
                                        openModal={ openModal }
                                        addLabel="Tambah Detail Pembelian" />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <br />
                <div className="row">
                    <div className="col-md-6"></div>
                    <div className="col-md-6"><Note isAction={ true } /></div>
                </div>

                <br />

                {
                    purchase.items && 

                    <div className="col-lg-12 mb-4">
                        <div className="row">
                            <div className="table-responsive">
                                <table className="table table-striped table-hover card-text">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Kode Barang</th>
                                            <th>Nama Barang</th>
                                            <th>Satuan</th>
                                            <th>Quantity</th>
                                            <th>Harga Beli</th>
                                            <th>Sub Total</th>
                                            {
                                                isNew &&
                                                <th>Detail</th>
                                            }
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            purchase.items.map((item, index) => {
                                                return(
                                                    <tr key={ index }>
                                                        <th scope="row">{ index + 1 }</th>
                                                        <td>{ item.code }</td>
                                                        <td>{ item.name }</td>
                                                        <td>{ item.unit }</td>
                                                        <td>{ item.qty }</td>
                                                        <td><TextNumber value={ item.purchasePrice } /></td>
                                                        <td><TextNumber value={ item.qty * item.purchasePrice } /></td>
                                                        {
                                                            isNew &&
                                                            <td>
                                                                <button className={`btn ${constant.BTN_CLASS_DETAIL}`} onClick={ (e) => onViewDetail(e, index) }>Detail</button>
                                                            </td>
                                                        }
                                                    </tr>
                                                );
                                            })
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                }

            </div>
        </React.Fragment>
    );
};

export default Action;
